import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import {
  CreateJobDescription,
  DeleteJobDescription,
  GetJobDescriptions,
  GetSingleJobDescription,
  UpdateJobDescription,
} from './job-description.actions';
import { JobDescriptionSevice } from './job-description.service';
import { IJobDescription } from './job-description.interface';
import { catchError, tap } from 'rxjs';

export class JobDescriptionStateModel {
  descriptions!: IJobDescription[];
  description!: IJobDescription | undefined;
  loading!: boolean;
  loadingUpdate!: boolean;
  error!: string | null;
}

const defaults = {
  descriptions: [],
  description: undefined,
  loading: false,
  loadingUpdate: false,
  error: null,
};

@State<JobDescriptionStateModel>({
  name: 'jobDescription',
  defaults,
})
@Injectable()
export class JobDescriptionState {
  jobDescriptionSevice: JobDescriptionSevice = inject(JobDescriptionSevice);

  @Selector()
  static getJobDescription(state: JobDescriptionStateModel) {
    return state.descriptions;
  }

  @Selector()
  static getSingleDesc(state: JobDescriptionStateModel) {
    return state.description;
  }

  @Selector()
  static getLoading(state: JobDescriptionStateModel) {
    return state.loading;
  }

  @Selector()
  static getLoadingUpdate(state: JobDescriptionStateModel) {
    return state.loadingUpdate;
  }

  @Selector()
  static getError(state: JobDescriptionStateModel) {
    return state.error;
  }

  @Action(GetJobDescriptions)
  get(ctx: StateContext<JobDescriptionStateModel>, action: GetJobDescriptions) {
    const state = ctx.getState();

    ctx.patchState({ loading: true });
    return this.jobDescriptionSevice.getJobDescriptions().pipe(
      tap((res) => {
        ctx.patchState({
          descriptions: res as IJobDescription[],
          loading: false,
          error: null,
        });
      }),
      catchError((err) => {
        ctx.patchState({
          loading: false,
          error: err.message,
        });
        throw err;
      })
    );
  }

  @Action(GetSingleJobDescription)
  getSingle(
    ctx: StateContext<JobDescriptionStateModel>,
    action: GetSingleJobDescription
  ) {
    const state = ctx.getState();
    ctx.patchState({ loading: true });
    return this.jobDescriptionSevice.getSingle(action.id).pipe(
      tap((res) => {
        ctx.patchState({
          description: res as IJobDescription,
          loading: false,
          error: null,
        });
      }),
      catchError((err) => {
        ctx.patchState({
          loading: false,
          error: err.message,
        });
        throw err;
      })
    );
  }

  @Action(CreateJobDescription)
  createDesc(
    ctx: StateContext<JobDescriptionStateModel>,
    action: CreateJobDescription
  ) {
    const state = ctx.getState();

    ctx.patchState({ loading: true });

    return this.jobDescriptionSevice.createDesc(action.input).pipe(
      tap((res) => {
        ctx.patchState({
          descriptions: [...state.descriptions, res as IJobDescription],
          loading: false,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: false,
          error: error.message,
        });
        throw error;
      })
    );
  }

  @Action(UpdateJobDescription)
  updateDesc(
    ctx: StateContext<JobDescriptionStateModel>,
    action: UpdateJobDescription
  ) {
    const state = ctx.getState();

    ctx.patchState({ loadingUpdate: true });

    return this.jobDescriptionSevice.updateDesc(action.input).pipe(
      tap((res) => {
        ctx.patchState({
          descriptions: state.descriptions.map((desc) =>
            desc.id === action.input.id ? { ...desc, res } : desc
          ),
          description: res as IJobDescription,
          loadingUpdate: false,
          error: null,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loadingUpdate: false,
          error: error.message,
        });
        throw error;
      })
    );
  }

  @Action(DeleteJobDescription)
  deleteDesc(
    ctx: StateContext<JobDescriptionStateModel>,
    action: DeleteJobDescription
  ) {
    const state = ctx.getState();

    ctx.patchState({ loading: true });

    return this.jobDescriptionSevice.deleteDesc(action.id).pipe(
      tap((res) => {
        ctx.patchState({
          descriptions: state.descriptions.filter((d) => d.id !== action.id),
          loading: false,
          error: null,
        });
      }),
      catchError((err) => {
        ctx.patchState({
          loading: false,
          error: err.message,
        });
        throw err;
      })
    );
  }
}
